import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, isStudent } from '../../../utils/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import styles from './StudentManito.module.css';

const StudentManito: React.FC = () => {
  const { currentUser } = useAuth();
  const student = currentUser as Student;
  const [targetName, setTargetName] = useState<string | null>(null);
  const [targetNumber, setTargetNumber] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const assignmentKey = useMemo(() => {
    if (!isStudent(currentUser) || !student?.schoolId || !student?.classId || !student?.uid) return null;
    return {
      schoolId: student.schoolId,
      classId: student.classId,
      uid: student.uid,
    };
  }, [currentUser, student?.schoolId, student?.classId, student?.uid]);

  useEffect(() => {
    if (!assignmentKey) {
      setLoading(false);
      return;
    }
    const ref = doc(
      db,
      'schools',
      assignmentKey.schoolId,
      'classes',
      assignmentKey.classId,
      'manitoAssignments',
      assignmentKey.uid
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setLoading(false);
        setError(null);
        if (!snap.exists()) {
          setTargetName(null);
          setTargetNumber(null);
          return;
        }
        const data = snap.data();
        setTargetName((data.targetName as string) || null);
        const tn = data.targetNumber;
        let num: number | null = null;
        if (typeof tn === 'number' && !Number.isNaN(tn)) num = tn;
        else if (typeof tn === 'string') {
          const p = parseInt(tn, 10);
          if (!Number.isNaN(p)) num = p;
        }
        setTargetNumber(num);
      },
      (err) => {
        console.error('마니또 로드 오류:', err);
        setLoading(false);
        setError('정보를 불러오지 못했습니다.');
      }
    );
    return () => unsub();
  }, [assignmentKey]);

  if (!isStudent(currentUser)) {
    return (
      <div style={{ padding: '16px 12px', color: 'var(--Color-text-normal)', fontFamily: 'var(--font-family-primary)' }}>
        학생만 이용할 수 있는 페이지입니다.
      </div>
    );
  }

  if (!student.classId) {
    return (
      <div style={{ padding: '16px 12px', color: 'var(--Color-text-normal)', fontFamily: 'var(--font-family-primary)' }}>
        학급에 소속된 계정에서만 확인할 수 있습니다.
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>내 마니또</h1>
        <p className={styles.pageDesc}>
          담임 선생님이 매칭해 주시면, 여기에서 <strong>내가 챙겨 줄 친구</strong>를 확인할 수 있어요.
        </p>
      </header>

      <Card
        width="100%"
        padding="clamp(28px, 6vw, 56px) clamp(16px, 5vw, 40px)"
        borderRadius="16px"
        background="var(--Color-background-normal)"
        gap="0"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        className={styles.heroCard}
        style={{
          boxSizing: 'border-box',
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div className={`${styles.heroInner} ${styles.loadingText}`}>불러오는 중…</div>
        ) : error ? (
          <div className={styles.heroInner} role="alert" style={{ color: 'var(--Color-text-strong)', fontSize: '18px' }}>
            {error}
          </div>
        ) : targetName ? (
          <div className={styles.heroInner}>
            <div className={styles.hint}>이번 마니또는…</div>
            <div className={styles.nameMain}>
              {targetNumber != null && targetNumber > 0 ? `${targetNumber}번 ` : ''}
              {targetName}
            </div>
            <div className={styles.footerHint}>친구에게 몰래 잘 챙겨 주세요!</div>
          </div>
        ) : (
          <div className={`${styles.heroInner} ${styles.emptyState}`}>
            아직 마니또가 정해지지 않았어요.
            <br />
            선생님께 말씀드린 뒤 다시 들어와 보세요.
          </div>
        )}
      </Card>
    </div>
  );
};

export default StudentManito;
